"use client";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useRouter } from "next/navigation";
import { AuthType, LoginDataProps } from "@/interface/authentication";
import axios from "axios";
import toast from "react-hot-toast";
import { LOGIN_API } from "@/constants";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, AppState } from "@/store/configureStore";
import { updateUser } from "@/store/slices/userSlice";

const useValidateLogin = (type: AuthType) => {
	const router = useRouter();
	const { lastNonAuthRoute } = useSelector((state: AppState) => state.user);
	const dispatch = useDispatch<AppDispatch>();
	const loginSchema = yup.object({
		email:
			type === AuthType.EMAIL
				? yup
						.string()
						.email("Invalid email address")
						.required("Email is required")
				: yup.string(),
		password: yup.string().required("Password is required"),
		phone_number:
			type === AuthType.PHONE_NUMBER
				? yup.string().required("Phone number is required")
				: yup.string(),
	});

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
		resetField,
	} = useForm({
		resolver: yupResolver(loginSchema),
	});

	const handleLogin = async (data: LoginDataProps) => {
		try {
			const response = await axios.post(LOGIN_API, {
				emailOrPhone: type === AuthType.EMAIL ? data.email : data.phone_number,
				password: data.password,
			});
			// console.log(response?.data);
			toast.remove();
			toast.success(response?.data?.message);
			dispatch(
				updateUser({
					name: response?.data?.data?.user?.fullName,
					id: response?.data?.data?.user?.id,
					avatarKey: response?.data?.data?.user?.avatarkey,
					email: response?.data?.data?.user?.email,
					phoneNumber: response?.data?.data?.user?.phoneNumber,
					token: response?.data?.data?.token,
					isVerified: response?.data?.data?.user?.verified,
					dateOfBirth: response?.data?.data?.user?.dateOfBirth,
					addresses: response?.data?.data?.user?.addresses,
				})
			);
			if (lastNonAuthRoute && lastNonAuthRoute === "/checkout") {
				router.push("/checkout");
			} else {
				router.push("/");
			}
		} catch (error: any) {
			toast.error(
				`An error occured trying to login: ${error?.response?.data?.error}`
			);
			router.push("/login");
		}
	};

	useEffect(() => {
		resetField(type === AuthType.EMAIL ? AuthType.PHONE_NUMBER : AuthType.EMAIL);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [type]);

	return {
		register,
		handleSubmit,
		errors,
		handleLogin,
		reset,
		type,
	};
};

export default useValidateLogin;
